// Small helpers for keyboard- and screen-reader-friendly markup (see
// ACCESSIBILITY.md). Prefer a real <button> / <a href>; reach for these only
// when the element has to stay a div / span / tr / td (layout, drag handles,
// table rows that open a record).

// Enter and Space run `handler`, like a native button. Space is stopped from
// scrolling the page.
export const onKeyActivate = handler => event => {
  if (event.target !== event.currentTarget) return;
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handler?.(event);
  }
};

// Props that make a non-interactive element behave as a button:
//   <div className='card' {...buttonProps(open, 'Open tender 123')} />
// `label` is the accessible name when the element has no readable text.
export const buttonProps = (onClick, label, extra = {}) => ({
  role: 'button',
  tabIndex: 0,
  onClick,
  onKeyDown: onKeyActivate(onClick),
  ...(label ? { 'aria-label': label } : {}),
  ...extra,
});

// Same for a toggle (expand / collapse, select): adds aria-pressed or
// aria-expanded.
export const toggleProps = (onClick, state, label, kind = 'pressed') => ({
  ...buttonProps(onClick, label),
  [`aria-${kind}`]: Boolean(state),
});

// Polite announcement for results that appear without a page change
// ("12 results", "Saved"). Renders into a shared visually-hidden live region.
let region;
export const announce = (message, politeness = 'polite') => {
  if (typeof document === 'undefined' || !message) return;
  if (!region || !region.isConnected) {
    region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', politeness);
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
  }
  region.setAttribute('aria-live', politeness);
  // clearing first makes repeats of the same text announce again
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 50);
};
